import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Loader2, Building2, Users2, Boxes } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  ceo: "CEO (Super Admin)", dept_head: "Department Head",
  center_manager: "Center Manager", viewer: "Viewer",
};

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

/* ---------------- Centers ---------------- */
function CentersTab() {
  const { centers, refreshMeta } = useApp();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const start = (c) => {
    setEditing(c);
    setForm(c || { name: "", code: "", type: "Center", city: "", status: "active", avg_ticket: 50000, revenue_target: 10000000 });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, avg_ticket: Number(form.avg_ticket), revenue_target: Number(form.revenue_target) };
      if (editing?.id) await api.put(`/centers/${editing.id}`, payload);
      else await api.post("/centers", payload);
      toast.success(editing?.id ? "Center updated" : "Center added");
      setOpen(false);
      setTimeout(refreshMeta, 250);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    try { await api.delete(`/centers/${c.id}`); toast.success("Center deleted"); refreshMeta(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          <h3 className="font-display font-semibold">Centers & Units</h3>
        </div>
        <Button size="sm" onClick={() => start(null)} data-testid="add-center-button">
          <Plus className="h-4 w-4 mr-1" /> Add Center
        </Button>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Code</TableHead><TableHead>Type</TableHead>
              <TableHead>City</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {centers.map((c) => (
              <TableRow key={c.id} data-testid={`center-row-${c.code}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="font-mono text-xs">{c.code}</TableCell>
                <TableCell>{c.type}</TableCell>
                <TableCell className="text-muted-foreground">{c.city}</TableCell>
                <TableCell><Badge variant="secondary" className="capitalize">{c.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => start(c)} data-testid={`edit-center-${c.code}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <DeleteBtn onConfirm={() => remove(c)} name={c.name} testid={`delete-center-${c.code}`} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="center-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Center" : "Add Center"}</DialogTitle>
            <DialogDescription>Centers and units power revenue, dashboard filters and reports.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name"><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="center-name-input" /></Field>
            <Field label="Code"><Input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="center-code-input" /></Field>
            <Field label="Type">
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="center-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="HQ">HQ</SelectItem><SelectItem value="Center">Center</SelectItem><SelectItem value="Institute">Institute</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="City"><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
            <Field label="Avg Ticket (₹)"><Input type="number" value={form.avg_ticket || 0} onChange={(e) => setForm({ ...form, avg_ticket: e.target.value })} /></Field>
            <Field label="Revenue Target (₹)"><Input type="number" value={form.revenue_target || 0} onChange={(e) => setForm({ ...form, revenue_target: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name || !form.code} data-testid="save-center-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ---------------- Departments ---------------- */
function DepartmentsTab() {
  const { departments, refreshMeta } = useApp();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const start = (d) => { setEditing(d); setForm(d || { name: "", icon: "Building2", description: "" }); setOpen(true); };
  const save = async () => {
    setSaving(true);
    try {
      if (editing?.id) await api.put(`/departments/${editing.id}`, form);
      else await api.post("/departments", form);
      toast.success(editing?.id ? "Department updated" : "Department added");
      setOpen(false); setTimeout(refreshMeta, 250);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (d) => {
    try { await api.delete(`/departments/${d.id}`); toast.success("Department deleted"); refreshMeta(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Boxes className="h-5 w-5 text-primary" />
          <h3 className="font-display font-semibold">Departments</h3>
        </div>
        <Button size="sm" onClick={() => start(null)} data-testid="add-department-button">
          <Plus className="h-4 w-4 mr-1" /> Add Department
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {departments.map((d) => (
          <div key={d.id} className="rounded-xl border border-border p-4 flex items-start justify-between" data-testid={`department-row-${d.id}`}>
            <div className="min-w-0">
              <p className="font-semibold">{d.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{d.description}</p>
            </div>
            <div className="flex shrink-0">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => start(d)} data-testid={`edit-department-${d.id}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <DeleteBtn onConfirm={() => remove(d)} name={d.name} testid={`delete-department-${d.id}`} />
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="department-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Department" : "Add Department"}</DialogTitle>
            <DialogDescription>Departments group KPIs, daily check-ins and issues.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Name"><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="department-name-input" /></Field>
            <Field label="Description"><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="save-department-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ---------------- Users ---------------- */
function UsersTab() {
  const { centers, departments } = useApp();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [u, r] = await Promise.all([api.get("/users"), api.get("/meta/roles")]);
      setUsers(u.data); setRoles(r.data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const start = (u) => {
    setEditing(u);
    setForm(u ? { ...u, password: "" } : { name: "", email: "", role: "viewer", password: "", department_id: null, center_id: null });
    setOpen(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, role: form.role,
        department_id: form.department_id || null, center_id: form.center_id || null,
      };
      if (form.password) payload.password = form.password;
      if (editing?.id) await api.put(`/users/${editing.id}`, payload);
      else await api.post("/users", { ...payload, email: form.email, password: form.password || "Youngness@123" });
      toast.success(editing?.id ? "User updated" : "User added");
      setOpen(false); setTimeout(load, 250);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (u) => {
    try { await api.delete(`/users/${u.id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const centerName = (id) => centers.find((c) => c.id === id)?.name || "—";
  const deptName = (id) => departments.find((d) => d.id === id)?.name || "—";

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users2 className="h-5 w-5 text-primary" />
          <h3 className="font-display font-semibold">Users & Roles</h3>
        </div>
        <Button size="sm" onClick={() => start(null)} data-testid="add-user-button">
          <Plus className="h-4 w-4 mr-1" /> Add User
        </Button>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead>
              <TableHead>Center</TableHead><TableHead>Department</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-muted-foreground text-xs">{u.email}</TableCell>
                <TableCell><Badge variant="secondary">{ROLE_LABELS[u.role] || u.role}</Badge></TableCell>
                <TableCell>{centerName(u.center_id)}</TableCell>
                <TableCell>{deptName(u.department_id)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => start(u)} data-testid={`edit-user-${u.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <DeleteBtn onConfirm={() => remove(u)} name={u.name} testid={`delete-user-${u.id}`} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit User" : "Add User"}</DialogTitle>
            <DialogDescription>Assign a role, center and department to control access.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full name"><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></Field>
            <Field label="Email">
              <Input type="email" value={form.email || ""} disabled={!!editing?.id}
                onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
            </Field>
            <Field label="Role">
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label={editing?.id ? "Reset password (optional)" : "Password"}>
              <Input type="password" value={form.password || ""} placeholder={editing?.id ? "Leave blank to keep" : "Youngness@123"}
                onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
            </Field>
            <Field label="Center">
              <Select value={form.center_id || "none"} onValueChange={(v) => setForm({ ...form, center_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="user-center-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {centers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Department">
              <Select value={form.department_id || "none"} onValueChange={(v) => setForm({ ...form, department_id: v === "none" ? null : v })}>
                <SelectTrigger data-testid="user-department-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name || (!editing?.id && !form.email)} data-testid="save-user-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function DeleteBtn({ onConfirm, name, testid }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600 hover:text-rose-700" data-testid={testid}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete "{name}"?</AlertDialogTitle>
          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-button">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function Settings() {
  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Administration</p>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Settings & Configuration</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage centers, departments, users and roles. Everything is customizable here.</p>
      </div>
      <Tabs defaultValue="centers">
        <TabsList data-testid="settings-tabs">
          <TabsTrigger value="centers" data-testid="tab-centers">Centers</TabsTrigger>
          <TabsTrigger value="departments" data-testid="tab-departments">Departments</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users & Roles</TabsTrigger>
        </TabsList>
        <TabsContent value="centers" className="mt-4"><CentersTab /></TabsContent>
        <TabsContent value="departments" className="mt-4"><DepartmentsTab /></TabsContent>
        <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>
      </Tabs>
    </div>
  );
}
